import React, { useEffect } from 'react';
import { ReactSVG } from 'react-svg';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageContent, Container, Spinner, Button, ButtonIcon, Row, Pagination } from 'components';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import { isLoading, globalPropTypes, checkPermissions } from 'helpers';
import { formatDateRange } from 'helpers/date';
import { useAccessGuard } from 'hooks';
import {
  loadMaMeetingsCreator,
  setMaMeetingsPageCreator,
  setMaMeetingsSortCreator,
  setMaMeetingsFiltersCreator,
} from 'store/actionsCreators';
import { iconList } from 'assets';
import MeetingsTable, { InvoiceIcon, StatusNote } from '../Meetings.styled';
import { flagKeys, flagColumnKeys } from '../Application/flag';

/**
 * An accepted application is settled and only readable. A rejected one is not:
 * the Member Association reworks it and sends it in again, so it keeps its edit
 * action while the meeting is open.
 */
const DECIDED = ['ACCEPTED'];

/** The list names the month a meeting is held in, not the exact day. */
const MONTH_FORMAT = 'MMMM YYYY';

/**
 * Meetings of a single type (COD or CSP) a Member Association can apply to.
 *
 * Both meeting types use this screen; they are separate entries in the
 * navigation only to keep the two flows visually apart.
 */
const MeetingsList = ({
  meetingType,
  loading,
  list,
  loadList,
  setPage,
  setSort,
  setFilters,
  userPermissions,
}) => {
  const { filters, items, page, pages, perPage, sort, totalItems } = list;
  const { t } = useTranslation();
  const history = useHistory();

  // Permissions arrive after the first render, and until they do there is
  // nothing to deny yet - denying early would throw out a user who is allowed.
  const permissionsKnown = !!userPermissions;
  const canView =
    !permissionsKnown ||
    checkPermissions({ roles: [`ma_meeting_view_${meetingType}`] }, userPermissions);

  // A user who may read the other meeting type is moved one tab along rather
  // than out of the section over a screen they cannot open.
  const allowedType = permissionsKnown
    ? ['cod', 'csp'].find((type) =>
        checkPermissions({ roles: [`ma_meeting_view_${type}`] }, userPermissions),
      )
    : null;

  const [allowed, renderAccessDeniedModal] = useAccessGuard(
    canView,
    '/ma',
    'page.ma.meetings.accessDenied',
    allowedType ? `/ma/meetings/${allowedType}` : null,
  );

  useEffect(() => {
    if (!allowed) return;

    setPage(1);
    loadList();
  }, [sort, allowed]);

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    setSort(direction ? `${key}.${direction}` : null);
  };

  const basePath = `/ma/meetings/${meetingType}`;
  // Every row here is the reader's own application, and they hold one seat, so
  // the whole column carries the name that seat gives the flag. Before they
  // applied anywhere there is nothing to read it from and the meeting type
  // decides instead.
  const ownFlag = (items.find((item) => item.application) || {}).application;
  const columnFlag = flagColumnKeys(meetingType, ownFlag ? ownFlag.flag : null);

  const statusOptions = [
    { value: 'DRAFT', label: t('page.ma.meetings.list.statusDraft') },
    { value: 'SUBMITTED', label: t('page.ma.meetings.list.statusSubmitted') },
    { value: 'CHAIR_APPROVED', label: t('page.ma.meetings.list.statusChairApproved') },
    { value: 'ACCEPTED', label: t('page.ma.meetings.list.statusAccepted') },
    { value: 'REJECTED', label: t('page.ma.meetings.list.statusRejected') },
    { value: 'NONE', label: t('page.ma.meetings.list.statusNone') },
  ];

  // Nothing to show before the reader applied themselves. What the flag is
  // called comes with the application: an observer is verified where a delegate
  // is invoiced, and the two sit in the same column.
  const renderFlag = (item) => {
    if (!item.application) return <span>-</span>;

    const on = !!item.application.verified;
    const keys = flagKeys(item.application.flag);
    const icon = on ? 'checkThin' : 'close';

    return (
      <InvoiceIcon paid={on} title={t(on ? keys.onLong : keys.offLong)}>
        <ReactSVG src={iconList[icon]} />
      </InvoiceIcon>
    );
  };

  /**
   * What the reader may do with their own application, plus the way into the
   * ones their colleagues sent: everybody holding a seat applies separately
   * and reads the applications of their Member Association.
   */
  const renderActions = (item) => {
    // Once IPMA accepted an application it is frozen, so the row offers the
    // read only view even while the meeting is still open.
    const decided = item.application && DECIDED.includes(item.application.status);

    const colleagues =
      item.applicationCount > (item.application ? 1 : 0) ? (
        <ButtonIcon
          icon="users"
          secondary
          as={Button}
          title={t('page.ma.meetings.list.viewAll', { count: item.applicationCount })}
          onClick={() => history.push(`${basePath}/applications/${item.id}`)}
        />
      ) : null;

    if (item.canApply && !decided) {
      return (
        <>
          {item.application ? (
            <ButtonIcon
              icon="edit"
              secondary
              as={Button}
              title={t('page.ma.meetings.list.edit')}
              onClick={() => history.push(`${basePath}/application/${item.application.id}/edit`)}
            />
          ) : (
            <Button
              style={{ width: 'auto' }}
              secondary
              onClick={() => history.push(`${basePath}/application/create/${item.id}`)}
            >
              {t('page.ma.meetings.list.create')}
            </Button>
          )}
          {colleagues}
        </>
      );
    }

    // A closed meeting only keeps a read only view, and only for somebody who
    // applied to it or whose colleagues did.
    if (item.application) {
      return (
        <>
          <ButtonIcon
            icon="eye"
            secondary
            as={Button}
            title={t('page.ma.meetings.list.view')}
            onClick={() => history.push(`${basePath}/application/${item.application.id}/view`)}
          />
          {colleagues}
        </>
      );
    }

    return colleagues || <span>-</span>;
  };

  if (!allowed) {
    return <PageContent>{renderAccessDeniedModal()}</PageContent>;
  }

  return (
    <PageContent>
      <Container>
        <MeetingsTable tablebreakpoint="lg" actionsWidth="12rem">
          <MeetingsTable.Head>
            <MeetingsTable.Row>
              <MeetingsTable.Heading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.name || ''}
                  onFilter={(val) => handleColumnFilterChange('name', val)}
                  filterPlaceholder={t('page.ma.meetings.list.namePlaceholder')}
                  label={t('page.ma.meetings.list.name')}
                  sort
                  sortDirection={getSortDirection('name')}
                  onSort={(dir) => handleColumnSort('name', dir)}
                />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell
                  filter
                  filterType="datepicker-range"
                  filterValueFrom={filters.dateFrom || ''}
                  filterValueTo={filters.dateTo || ''}
                  onFilterFrom={(val) => handleColumnFilterChange('dateFrom', val)}
                  onFilterTo={(val) => handleColumnFilterChange('dateTo', val)}
                  filterPlaceholder={t('page.ma.meetings.list.datePlaceholder')}
                  label={t('page.ma.meetings.list.date')}
                  sort
                  sortDirection={getSortDirection('date')}
                  onSort={(dir) => handleColumnSort('date', dir)}
                />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell
                  filter
                  filterType="select"
                  filterValue={filters.status || ''}
                  onFilter={(val) => handleColumnFilterChange('status', val)}
                  filterOptions={statusOptions}
                  filterPlaceholder={t('page.ma.meetings.list.statusPlaceholder')}
                  label={t('page.ma.meetings.list.status')}
                  sort
                  sortDirection={getSortDirection('status')}
                  onSort={(dir) => handleColumnSort('status', dir)}
                />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell label={t(columnFlag.column)} isAction />
              </MeetingsTable.Heading>
              <MeetingsTable.Heading>
                <TableHeaderCell label={t('page.ma.meetings.list.actions')} isAction />
              </MeetingsTable.Heading>
            </MeetingsTable.Row>
          </MeetingsTable.Head>
          <MeetingsTable.Body>
            {loading ? (
              <MeetingsTable.Row>
                <MeetingsTable.Cell colSpan={5}>
                  <Spinner />
                </MeetingsTable.Cell>
              </MeetingsTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <MeetingsTable.Row>
                <MeetingsTable.Cell colSpan={5}>{t('global.noResultsFound')}</MeetingsTable.Cell>
              </MeetingsTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <MeetingsTable.Row key={item.id}>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>{t('page.ma.meetings.list.name')}:</MeetingsTable.Label>
                      {item.name}
                    </MeetingsTable.Cell>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>{t('page.ma.meetings.list.date')}:</MeetingsTable.Label>
                      {formatDateRange(item.dateFrom, item.dateTo, MONTH_FORMAT)}
                    </MeetingsTable.Cell>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>
                        {t('page.ma.meetings.list.status')}:
                      </MeetingsTable.Label>
                      {item.application
                        ? item.application.statusLabel
                        : t('page.ma.meetings.list.statusNone')}
                      {item.application && item.application.awaitsChair ? (
                        <StatusNote>
                          {t('page.ma.meetings.application.awaitsChairShort')}
                        </StatusNote>
                      ) : null}
                    </MeetingsTable.Cell>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>{t(columnFlag.column)}:</MeetingsTable.Label>
                      <Row style={{ justifyContent: 'center' }}>{renderFlag(item)}</Row>
                    </MeetingsTable.Cell>
                    <MeetingsTable.Cell>
                      <MeetingsTable.Label>
                        {t('page.ma.meetings.list.actions')}:
                      </MeetingsTable.Label>
                      <Row style={{ gap: '3px', justifyContent: 'center' }}>
                        {renderActions(item)}
                      </Row>
                    </MeetingsTable.Cell>
                  </MeetingsTable.Row>
                ))
              : null}
          </MeetingsTable.Body>
        </MeetingsTable>
        <Pagination
          styled
          currentPage={page}
          totalPages={pages}
          totalItems={totalItems}
          perPage={perPage}
          onChange={(payload) => {
            setPage(payload);
            loadList();
          }}
        />
      </Container>
    </PageContent>
  );
};

MeetingsList.propTypes = {
  meetingType: PropTypes.oneOf(['cod', 'csp']).isRequired,
  userPermissions: globalPropTypes.UserPermissions.isRequired,
  loading: PropTypes.bool,
  list: globalPropTypes.ListPropsTypes.isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
};

MeetingsList.defaultProps = {
  loading: false,
};

const mapStateToProps = ({ loading, ma, user }, { meetingType }) => ({
  // The meeting sagas report progress under the global loading type, the same
  // one the page loader in Root listens to.
  loading: isLoading(loading, 'GLOBAL'),
  list: ma.meetings[meetingType],
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch, { meetingType }) => ({
  loadList: () => dispatch(loadMaMeetingsCreator(meetingType)),
  setPage: (payload) => dispatch(setMaMeetingsPageCreator(meetingType, payload)),
  setSort: (payload) => dispatch(setMaMeetingsSortCreator(meetingType, payload)),
  setFilters: (payload) => dispatch(setMaMeetingsFiltersCreator(meetingType, payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MeetingsList);
